import React from 'react';

import { ForgeUIExtensionPointProvider } from '@atlassian/forge-ui/provider/ForgeUIExtensionPointProvider';
import type { ForgeUIExtensionQueryOptions } from '@atlassian/forge-ui/provider/ForgeUIExtensionPointProvider';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fg } from '@confluence/feature-gating';

import { getProductEnvironment } from './utils/environmentMapping.ts';
import type { Extension } from './types.ts';
import { useExtensionList } from './hooks/useExtensionList.tsx';
import type { ForgeModules } from './ForgeModules.ts';
import { FORGE_MODULE_ATTACHMENT_ACTION } from './ForgeModuleType.ts';

export interface DecoratorProps {
	title: string;
	extension: Extension;
	children?: React.ReactNode;
}

export type RenderForgeExtensions = (
	extensions: ReadonlyArray<Extension>,
	loading: boolean,
	error?: Error,
) => any;

type ForgeUIExtensionProviderWrapperProps = React.PropsWithChildren<{
	moduleType: string;
	/**
	 * @deprecated No longer invoked. The provider now renders for every user, including
	 * anonymous (unlicensed) ones, so there is no "no access" short-circuit left to fall back
	 * from. Retained temporarily so existing call sites keep compiling; safe to delete along
	 * with its call sites in a follow-up change.
	 */
	noAccessRenderFallback?: RenderForgeExtensions;
}>;

export const ForgeUIExtensionProviderWrapper = ({
	children,
	moduleType,
}: ForgeUIExtensionProviderWrapperProps): any => {
	const { environment } = useSessionData();

	return (
		<ForgeUIExtensionPointProvider
			analyticsWebClient={getAnalyticsWebClient()}
			environment={getProductEnvironment(environment)}
			product="confluence"
			page={moduleType}
		>
			{children}
		</ForgeUIExtensionPointProvider>
	);
};

type ForgeUIExtensionsWrapperProps = {
	moduleType: ForgeModules;
	render: RenderForgeExtensions;
	returnBlockedExtensions?: boolean;
	spaceContextOnly?: boolean;
	contentId?: string;
	spaceKey?: string;
};

export interface PropsNext {
	moduleType: ForgeModules;
	render: RenderForgeExtensions;
	queryOptions?: ForgeUIExtensionQueryOptions;
	returnBlockedExtensions?: boolean;
	spaceContextOnly?: boolean;
	contentId?: string;
	spaceKey?: string;
}

export const ForgeUIExtensions = ({
	moduleType,
	render,
	queryOptions,
	returnBlockedExtensions = false,
	spaceContextOnly,
	contentId,
	spaceKey,
}: PropsNext): any => {
	const attachmentActionContext =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		moduleType === FORGE_MODULE_ATTACHMENT_ACTION && fg('forge_attachment_action')
			? { contentId, spaceKey }
			: {};
	const { extensions, loading, error } = useExtensionList({
		moduleType,
		queryOptions,
		returnBlockedExtensions,
		...(spaceContextOnly ? { spaceContextOnly } : {}),
		...attachmentActionContext,
	});
	return render(sortExtensionsAlphabeticalTitle(extensions), loading, error);
};

export const ForgeUIExtensionsWrapper = (
	props: ForgeUIExtensionsWrapperProps,
): React.JSX.Element => {
	return (
		<ForgeUIExtensionProviderWrapper
			moduleType={props.moduleType}
			noAccessRenderFallback={props.render}
		>
			<ForgeUIExtensions {...props} />
		</ForgeUIExtensionProviderWrapper>
	);
};

export const sortExtensionsAlphabeticalTitle = (extensions: Extension[]): Extension[] => {
	return extensions.slice().sort((a, b) => {
		const titleA = a.properties.title;
		const titleB = b.properties.title;

		if (titleA && !titleB) {
			return -1;
		} else if (titleB && !titleA) {
			return 1;
		}
		if (!(titleA && titleB)) {
			return 0;
		}

		return titleA.localeCompare(titleB);
	});
};
